import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { MindARThree as MindARFaceThree } from 'mind-ar/dist/mindar-face-three.prod.js';
import { MindARThree as MindARPoseThree } from 'mind-ar/dist/mindar-pose-three.prod.js';
import { FilterAndCaptureButtons } from '../../utils/screenCapture';
import { makeEarlobeCompute, EARLOBE_DEFAULTS } from '../../utils/earlobeAnchor';
import config from '../../config/config';
import './Earrings.css';

// Fallback when manifest provides no physical dimensions or category:
// 0.30 of ear-to-ear width ≈ 45mm on a 150mm face, a typical medium drop/jhumka.
const EARRING_SCALE_FACTOR = 0.30;
const NECKLACE_SCALE = 0.5;
const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;

// Real-world ear-to-ear distance in mm. In MindAR canonical space 1 Three.js unit ≈ 1 cm,
// so the inter-earlobe anchor distance (~15.5 units) ≈ 155 mm on an adult face.
const FACE_REFERENCE_MM = 150;

const SIZE_PRESETS = {
  stud:       { heightMm: 10 },
  hoopSmall:  { heightMm: 25 },
  dropSmall:  { heightMm: 32 },
  dropMedium: { heightMm: 48 },
  chandelier: { heightMm: 65 },
};

// Safety clamps expressed as a fraction of smoothed faceWidth
const CATEGORY_LIMITS = {
  stud:       { min: 0.05, max: 0.18 },
  hoopSmall:  { min: 0.12, max: 0.30 },
  dropSmall:  { min: 0.15, max: 0.40 },
  dropMedium: { min: 0.25, max: 0.60 },
  chandelier: { min: 0.35, max: 0.85 },
};
const DEFAULT_LIMITS = { min: 0.05, max: 0.85 };

const getManifestNumber = (manifest, field, fallback) => {
  const val = Number(manifest?.[field]);
  return Number.isFinite(val) ? val : fallback;
};

const getManifestScale = (manifest, fallback) => {
  const scale = Number(manifest?.scale);
  return Number.isFinite(scale) && scale > 0 ? scale : fallback;
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// defaultScale is a vendor correction multiplier on top of the physical size (1.0 = no correction).
// Priority: physicalHeightMm → category preset → global default; defaultScale applied to all paths.
const resolveScaleFactor = (manifest) => {
  const ds = Number(manifest?.defaultScale);
  const multiplier = Number.isFinite(ds) && ds > 0 ? ds : 1.0;
  const heightMm = Number(manifest?.physicalHeightMm);
  if (Number.isFinite(heightMm) && heightMm > 0) return (heightMm / FACE_REFERENCE_MM) * multiplier;
  const preset = SIZE_PRESETS[manifest?.category];
  if (preset) return (preset.heightMm / FACE_REFERENCE_MM) * multiplier;
  return EARRING_SCALE_FACTOR * multiplier;
};

const hasPhysicalHeight = (manifest) => {
  const heightMm = Number(manifest?.physicalHeightMm);
  return Number.isFinite(heightMm) && heightMm > 0;
};

const resolveLimits = (manifest) => {
  const limits = CATEGORY_LIMITS[manifest?.category] ?? DEFAULT_LIMITS;
  return hasPhysicalHeight(manifest) ? { ...limits, min: 0 } : limits;
};

function Combo() {
  const { sku1, sku2 } = useParams();
  const category1 = 'necklaces';
  const category2 = 'earrings';

  // Face AR refs (earrings)
  const faceContainerRef = useRef(null);
  const faceMindARRef = useRef(null);
  const faceSceneRef = useRef(null);
  const faceAnchorRef = useRef({ leftEarAnchor: null, rightEarAnchor: null });
  const faceModelsRef = useRef({ faceMeshOccluder: null, leftEarring: null, rightEarring: null });

  // Pose AR refs (necklace)
  const poseContainerRef = useRef(null);
  const poseMindARRef = useRef(null);
  const poseSceneRef = useRef(null);
  const poseAnchorRef = useRef(null);
  const planeRef = useRef(null);
  const poseModelsRef = useRef({ necklace: null });

  const streamRef = useRef(null);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [error, setError] = useState(null);

  // Cartoon shader
  const cartoonMaterial = useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      lightDirection: { value: new THREE.Vector3(0, 1, 1).normalize() },
      toonLevel: { value: 4.0 },
      edge: { value: 0.4 }
    },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vPosition;

      void main() {
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 lightDirection;
      uniform float toonLevel;
      uniform float edge;

      varying vec3 vNormal;
      varying vec3 vPosition;

      void main() {
        float intensity = dot(vNormal, lightDirection);
        intensity = ceil(intensity * toonLevel) / toonLevel;

        float edgeIntensity = length(fwidth(vNormal));
        float isEdge = edgeIntensity > edge ? 0.0 : 1.0;

        vec3 toonColor = vec3(intensity * isEdge);
        gl_FragColor = vec4(toonColor, 0.5);
      }
    `,
    transparent: true,
    side: THREE.DoubleSide
  }), []);

  const faceTexture = useMemo(() => new THREE.TextureLoader().load('/assets/thumbnails/face-mask.png'), []);

  // Filter effect applies only to the face scene
  useEffect(() => {
    if (faceSceneRef.current && faceMindARRef.current) {
      const mindarThree = faceMindARRef.current;
      const { renderer, scene, camera } = faceSceneRef.current;

      const existingFaceMesh = scene.children.find(child => child.isFaceMesh);
      if (existingFaceMesh) {
        scene.remove(existingFaceMesh);
      }

      if (selectedFilter === 'none') {
        renderer.render(scene, camera);
      } else if (selectedFilter === 'cartoon') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material = cartoonMaterial;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      } else if (selectedFilter === 'animal') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material.map = faceTexture;
        faceMesh.material.transparent = true;
        faceMesh.material.needsUpdate = true;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      }
    }
  }, [selectedFilter, cartoonMaterial, faceTexture]);

  const initializeLights = (scene, camera) => {
    scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, 0, 1);
    camera.add(directionalLight);
    scene.add(camera);
    scene.add(new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1));
  };

  const makeTextureLoader = () => {
    const loader = new THREE.TextureLoader();
    return (url) => new Promise((resolve, reject) => {
      loader.load(
        url,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.encoding = THREE.sRGBEncoding;
          tex.minFilter = THREE.LinearFilter;
          tex.magFilter = THREE.LinearFilter;
          resolve(tex);
        },
        undefined,
        reject,
      );
    });
  };

  const fetchManifest = useCallback(async (category, sku) => {
    try {
      const res = await fetch(`${config.cdnUrl}/models/2D/${category}/${sku}/manifest.json`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, []);

  // Earrings — interpolated ear attachment anchors.
  // Touches no shared refs: StrictMode runs two setup() calls concurrently, and a
  // stale run writing refs would point them at a never-started instance. The
  // caller commits the returned objects to refs only if still mounted.
  const initializeFaceModels = useCallback(async (mindarThree, scene) => {
    const manifest = await fetchManifest(category2, sku2);
    console.log('[2D Combo] earrings manifest:', JSON.stringify(manifest, null, 2));
    console.log('[2D Combo] earrings resolveScaleFactor ->', resolveScaleFactor(manifest));
    console.log('[2D Combo] earrings resolveLimits ->', resolveLimits(manifest));

    const earlobeOffsets = {
      xOffset: getManifestNumber(manifest, 'earlobeXOffset', EARLOBE_DEFAULTS.xOffset),
      yOffset: getManifestNumber(manifest, 'earlobeYOffset', EARLOBE_DEFAULTS.yOffset),
      zDelta:  getManifestNumber(manifest, 'earlobeZDelta',  EARLOBE_DEFAULTS.zDelta),
    };

    const leftEarAnchor  = mindarThree.addDynamicAnchor(makeEarlobeCompute('left', earlobeOffsets));
    const rightEarAnchor = mindarThree.addDynamicAnchor(makeEarlobeCompute('right', earlobeOffsets));

    const faceMeshOccluder = mindarThree.addFaceMesh();
    faceMeshOccluder.material = new THREE.MeshBasicMaterial({
      colorWrite: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide,
    });
    faceMeshOccluder.renderOrder = -1;
    scene.add(faceMeshOccluder);

    const loadTexture = makeTextureLoader();
    const textureUrl = `${config.cdnUrl}/models/2D/${category2}/${sku2}/texture.png`;
    const earringsTexture = await loadTexture(textureUrl).catch(() => {
      console.log('Failed to load SKU-specific earrings texture, falling back to default');
      return loadTexture('/assets/2D/earrings/default.png');
    });

    const leftOffset  = manifest?.offsets?.left  ?? { x: 0, y: 0, z: 0.02 };
    const rightOffset = manifest?.offsets?.right ?? { x: 0, y: 0, z: 0.02 };
    const scaleFactor = resolveScaleFactor(manifest);
    const limits      = resolveLimits(manifest);
    const attachX     = getManifestNumber(manifest, 'anchorX', 0.5);
    const attachY     = getManifestNumber(manifest, 'anchorY', 0.08);
    const leftRotDeg  = getManifestNumber(manifest, 'leftRotationOffset', 0);
    const rightRotDeg = getManifestNumber(manifest, 'rightRotationOffset', 0);

    const { width, height } = earringsTexture.image;
    const aspectRatio = width / height;

    const material = new THREE.MeshBasicMaterial({
      map: earringsTexture,
      transparent: true,
      opacity: 1.0,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      alphaTest: 0.1,
    });

    // flipX mirrors the attachment point for the right ear; the animation loop
    // also applies scale.x < 0 so the texture itself is mirrored.
    const makeGeometry = (flipX) => {
      const geo = new THREE.PlaneGeometry(aspectRatio, 1);
      const ax = flipX ? 1 - attachX : attachX;
      geo.translate(-(ax - 0.5) * aspectRatio, -(0.5 - attachY), 0);
      return geo;
    };

    const leftPlane = new THREE.Mesh(makeGeometry(false), material.clone());
    leftPlane.position.set(leftOffset.x, leftOffset.y, leftOffset.z);
    leftPlane.rotation.z = (leftRotDeg * Math.PI) / 180;
    leftPlane.userData.scaleFactor = scaleFactor;
    leftPlane.userData.limitsMin   = limits.min;
    leftPlane.userData.limitsMax   = limits.max;
    leftPlane.renderOrder = 2;
    leftEarAnchor.group.add(leftPlane);

    const rightPlane = new THREE.Mesh(makeGeometry(true), material.clone());
    rightPlane.position.set(rightOffset.x, rightOffset.y, rightOffset.z);
    rightPlane.rotation.z = (rightRotDeg * Math.PI) / 180;
    rightPlane.userData.scaleFactor = scaleFactor;
    rightPlane.userData.limitsMin   = limits.min;
    rightPlane.userData.limitsMax   = limits.max;
    rightPlane.renderOrder = 2;
    rightEarAnchor.group.add(rightPlane);

    return {
      anchors: { leftEarAnchor, rightEarAnchor },
      models: { faceMeshOccluder, leftEarring: leftPlane, rightEarring: rightPlane },
    };
  }, [sku2, fetchManifest]);

  // Necklace — pose midpoint anchor between shoulders 11 and 12.
  // Same contract as initializeFaceModels: no shared refs, caller commits.
  const initializePoseModels = useCallback(async (necklaceAnchor) => {
    const loadTexture = makeTextureLoader();
    const textureUrl = `${config.cdnUrl}/models/2D/${category1}/${sku1}/texture.png`;

    const [loadedTexture, manifest] = await Promise.all([
      loadTexture(textureUrl).catch(() => {
        console.log('Failed to load SKU-specific necklace texture, falling back to default');
        return loadTexture('/assets/2D/necklaces/default.png');
      }),
      fetchManifest(category1, sku1),
    ]);
    console.log('[2D Combo] necklace manifest:', JSON.stringify(manifest, null, 2));
    console.log('[2D Combo] necklace resolved offset ->', manifest?.offsets ?? { x: 0, y: -0.05, z: -0.1 });
    console.log('[2D Combo] necklace resolved scale ->', getManifestScale(manifest, NECKLACE_SCALE));

    const { width, height } = loadedTexture.image;
    const aspectRatio = width / height;
    const offset = manifest?.offsets ?? { x: 0, y: -0.05, z: -0.1 };
    const scale = getManifestScale(manifest, NECKLACE_SCALE);

    const geometry = new THREE.PlaneGeometry(aspectRatio, 1);
    const material = new THREE.MeshBasicMaterial({
      map: loadedTexture,
      transparent: true,
      depthTest: true,
      depthWrite: true,
      side: THREE.DoubleSide,
      alphaTest: 0.1,
    });

    const plane = new THREE.Mesh(geometry, material);
    plane.scale.set(scale, scale, 1);
    plane.position.set(offset.x, offset.y, offset.z);
    plane.userData.baseScale = scale;
    necklaceAnchor.group.add(plane);
    return plane;
  }, [sku1, fetchManifest]);

  useEffect(() => {
    let mounted = true;
    const faceContainer = faceContainerRef.current;
    const poseContainer = poseContainerRef.current;
    const faceModelsSnapshot = faceModelsRef.current;

    const disposeUnstarted = (mindAR, container) => {
      if (!mindAR) return;
      try {
        mindAR.renderer.setAnimationLoop(null);
        mindAR.renderer.dispose();
        mindAR.renderer.forceContextLoss();
      } catch (err) {
        console.warn('Dispose error:', err);
      }
      container?.replaceChildren();
    };

    const setup = async () => {
      let faceMindAR = null;
      let poseMindAR = null;
      const abort = () => {
        disposeUnstarted(faceMindAR, faceContainer);
        disposeUnstarted(poseMindAR, poseContainer);
      };

      try {
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user' },
            audio: false,
          });
        } catch (err) {
          console.error('Camera access error:', err);
          setError('Please allow camera access to use this feature');
          return;
        }
        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        // Face AR — earrings
        faceMindAR = new MindARFaceThree({
          container: faceContainer,
          filterMinCF: 0.001,
          filterBeta: 1,
        });
        const { renderer: faceRenderer, scene: faceScene, camera: faceCamera } = faceMindAR;
        initializeLights(faceScene, faceCamera);
        const faceInit = await initializeFaceModels(faceMindAR, faceScene);
        if (!mounted) return abort();

        // Pose AR — necklace
        poseMindAR = new MindARPoseThree({
          container: poseContainer,
          filterMinCF: 0.001,
          filterBeta: 1,
        });
        const necklaceAnchor = poseMindAR.addMidpointAnchor(LEFT_SHOULDER, RIGHT_SHOULDER);
        const { renderer: poseRenderer, scene: poseScene, camera: poseCamera } = poseMindAR;
        initializeLights(poseScene, poseCamera);
        const necklacePlane = await initializePoseModels(necklaceAnchor);
        if (!mounted) return abort();

        {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
          }

          // Commit to refs only now that we know this run is the live one
          faceAnchorRef.current = faceInit.anchors;
          Object.assign(faceModelsRef.current, faceInit.models);
          poseAnchorRef.current = necklaceAnchor;
          planeRef.current = necklacePlane;
          poseModelsRef.current.necklace = necklacePlane;

          faceMindARRef.current = faceMindAR;
          faceSceneRef.current = { renderer: faceRenderer, scene: faceScene, camera: faceCamera };
          poseMindARRef.current = poseMindAR;
          poseSceneRef.current = { renderer: poseRenderer, scene: poseScene, camera: poseCamera };

          await faceMindAR.start();
          await poseMindAR.start();

          const leftPos = new THREE.Vector3();
          const rightPos = new THREE.Vector3();
          const anchorCol = new THREE.Vector3();
          let smoothedFaceWidth = 0;

          faceRenderer.setAnimationLoop(() => {
            const { leftEarAnchor, rightEarAnchor } = faceAnchorRef.current;
            const { leftEarring, rightEarring } = faceModelsRef.current;
            if (leftEarring && rightEarring && leftEarAnchor && rightEarAnchor) {
              leftEarAnchor.group.getWorldPosition(leftPos);
              rightEarAnchor.group.getWorldPosition(rightPos);
              const rawFaceWidth = leftPos.distanceTo(rightPos);

              if (rawFaceWidth > 0) {
                // EMA smoothing; cold-start on first valid sample
                smoothedFaceWidth = smoothedFaceWidth === 0
                  ? rawFaceWidth
                  : smoothedFaceWidth * 0.85 + rawFaceWidth * 0.15;

                const sf   = leftEarring.userData.scaleFactor ?? EARRING_SCALE_FACTOR;
                const lMin = leftEarring.userData.limitsMin   ?? DEFAULT_LIMITS.min;
                const lMax = leftEarring.userData.limitsMax   ?? DEFAULT_LIMITS.max;
                const leftZm  = leftEarring.userData.zoomMultiplier  ?? 1;
                const rightZm = rightEarring.userData.zoomMultiplier ?? 1;

                // MindAR bakes faceScale (metric face width, ~14) into the anchor
                // matrix, so children render faceScale× their local scale. Divide it
                // out so the computed size lands in world units (1 unit ≈ 1 cm).
                const anchorScale = anchorCol.setFromMatrixColumn(leftEarAnchor.group.matrix, 0).length() || 1;
                const leftS  = clamp(smoothedFaceWidth * sf * leftZm,  smoothedFaceWidth * lMin, smoothedFaceWidth * lMax) / anchorScale;
                const rightS = clamp(smoothedFaceWidth * sf * rightZm, smoothedFaceWidth * lMin, smoothedFaceWidth * lMax) / anchorScale;

                // Negative x on the right earring mirrors the texture horizontally
                leftEarring.scale.set(leftS, leftS, leftS);
                rightEarring.scale.set(-rightS, rightS, rightS);

                if (leftEarAnchor.group.visible) {
                  const leftZ  = leftEarAnchor.group.matrix.elements[14];
                  const rightZ = rightEarAnchor.group.matrix.elements[14];
                  const zDiff = leftZ - rightZ;
                  const leftOpacity  = clamp(1 + zDiff / (smoothedFaceWidth * 0.6), 0, 1);
                  const rightOpacity = clamp(1 - zDiff / (smoothedFaceWidth * 0.6), 0, 1);
                  leftEarring.material.opacity  = leftOpacity;
                  rightEarring.material.opacity = rightOpacity;
                  leftEarring.visible  = leftOpacity  > 0.03;
                  rightEarring.visible = rightOpacity > 0.03;
                }
              }
            }
            faceRenderer.render(faceScene, faceCamera);
          });

          poseRenderer.setAnimationLoop(() => {
            poseRenderer.render(poseScene, poseCamera);
          });
        }
      } catch (err) {
        console.error('Setup error:', err);
        setError('Failed to initialize AR experience');
      }
    };

    setup();

    return () => {
      mounted = false;
      const cleanup = async () => {
        try {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
          }
          if (faceMindARRef.current) {
            if (faceSceneRef.current) {
              const { scene, renderer, camera } = faceSceneRef.current;
              Object.values(faceModelsSnapshot).forEach((model) => {
                if (model && model.parent) model.parent.remove(model);
              });
              renderer.render(scene, camera);
              renderer.clear();
            }
            await faceMindARRef.current.stop();
            faceMindARRef.current.renderer.setAnimationLoop(null);
            faceMindARRef.current.renderer.dispose();
            faceMindARRef.current.renderer.forceContextLoss();
            faceMindARRef.current = null;
          }
          if (poseMindARRef.current) {
            if (planeRef.current && poseAnchorRef.current) {
              poseAnchorRef.current.group.remove(planeRef.current);
              planeRef.current.geometry?.dispose();
              planeRef.current.material?.map?.dispose();
              planeRef.current.material?.dispose();
              planeRef.current = null;
            }
            await poseMindARRef.current.stop();
            poseMindARRef.current.renderer.setAnimationLoop(null);
            poseMindARRef.current.renderer.dispose();
            poseMindARRef.current.renderer.forceContextLoss();
            poseMindARRef.current = null;
          }
          faceSceneRef.current = null;
          poseSceneRef.current = null;
          poseAnchorRef.current = null;
        } catch (err) {
          console.error('Cleanup error:', err);
        } finally {
          faceContainer?.replaceChildren();
          poseContainer?.replaceChildren();
        }
      };
      cleanup();
    };
  }, [initializeFaceModels, initializePoseModels]);

  const getRendererInfo = () => ({
    mindarThree: faceMindARRef.current,
    poseMindarThree: poseMindARRef.current,
    plane: planeRef.current,
    models: {
      leftEarring: faceModelsRef.current.leftEarring,
      rightEarring: faceModelsRef.current.rightEarring,
      necklace: poseModelsRef.current.necklace,
    },
  });

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          {error}
          <button
            onClick={() => { setError(null); window.location.reload(); }}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Face AR — earrings via face landmark tracking */}
      <div className="ar-container" ref={faceContainerRef}></div>
      {/* Pose AR — necklace via shoulder midpoint tracking; video hidden to avoid duplicate feed */}
      <div className="ar-container pose-overlay" ref={poseContainerRef}></div>
      <FilterAndCaptureButtons
        getRendererInfo={getRendererInfo}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        mode="combo"
      />
    </div>
  );
}

export default Combo;
